import { creatorbase } from '@round/api';
import styles from './CampaignInfo.module.css';
import { SkeletonizedValue } from '@round/ui-kit';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { asMoneyWithoutZeros } from 'helpers';
import { useBrand, useProject, useTeam } from '@round/creatorbase-helpers/api-hooks';
import cn from 'classnames';
import CampaignImage from 'Modules/Plans/Campaigns/components/CampaignImage/CampaignImage';

type Props = {
    campaign: creatorbase.Campaign | null | undefined;
    isLoading?: boolean;
    error?: string | null;
};

const CampaignInfo = ({ campaign, error, isLoading }: Props) => {
    const { getCurrencyById } = useCurrencies();
    const { data: project, isLoading: isProjectLoading } = useProject(campaign?.project_id);
    const { data: brand, isLoading: isBrandLoading } = useBrand(project?.brand_id);
    const { data: team, isLoading: isTeamLoading } = useTeam(project?.team_id);

    const campaignCurrency = getCurrencyById(campaign?.currency_id);
    const totalBudget = asMoneyWithoutZeros(campaign?.cost, campaignCurrency);
    const allocated = asMoneyWithoutZeros(campaign?.purchase_orders_allocated_amount, campaignCurrency);
    const outstandingValue = (campaign?.cost ?? 0) - (campaign?.purchase_orders_allocated_amount ?? 0);
    const outstanding = asMoneyWithoutZeros(outstandingValue, campaignCurrency);

    if (error) {
        return (
            <div className={styles.container}>
                <p className={styles.error}>{error}</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.campaign}>
                <CampaignImage
                    className={styles.image}
                    isLoading={isBrandLoading || isProjectLoading || isTeamLoading}
                    imageUrl={project?.image || ''}
                    managedBy={campaign?.managed_by ?? null}
                    platform={campaign?.platform ?? null}
                    alt={brand?.name ?? 'Brand'}
                />

                <div className={styles.campaignTitles}>
                    <p className={styles.title}>
                        <SkeletonizedValue isInitialized={!isLoading}>{campaign?.name}</SkeletonizedValue>
                    </p>

                    <p className={styles.subTitle}>
                        <SkeletonizedValue isInitialized={!(isBrandLoading || isProjectLoading)}>
                            {team?.name} - {brand?.name} - {project?.name}
                        </SkeletonizedValue>
                    </p>
                </div>
            </div>

            <p className={styles.amountRow}>
                <span className={styles.amountRowLabel}>Total Budget:</span>
                <span className={styles.amountRowValue}>
                    <SkeletonizedValue isInitialized={!isLoading} width="4rem">
                        {totalBudget}
                    </SkeletonizedValue>
                </span>
            </p>
            <p className={styles.amountRow}>
                <span className={styles.amountRowLabel}>Allocated:</span>
                <span className={styles.amountRowValue}>
                    <SkeletonizedValue isInitialized={!isLoading} width="4rem">
                        {allocated}
                    </SkeletonizedValue>
                </span>
            </p>
            <p className={styles.amountRow}>
                <span className={styles.amountRowLabel}>Outstanding:</span>
                <span
                    className={cn(styles.amountRowValue, {
                        [styles.statusBad]: outstandingValue > 0,
                        [styles.statusGood]: outstandingValue === 0,
                    })}
                >
                    <SkeletonizedValue isInitialized={!isLoading} width="4rem">
                        {outstanding}
                    </SkeletonizedValue>
                </span>
            </p>
        </div>
    );
};

export default CampaignInfo;
